import type { FieldFilter, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import { FilterOperator, getCollectionPrimaryKeyFields, getM2MStructure, isM2M, useFieldTypeTemplates } from '#layers/data-model/public';
import { nestFilterThroughRelations } from '#layers/filters/components/Filter/utils/filter-path';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { buildTerminalMatch } from '#layers/filters/components/Filter/utils/related-item-match';
import { useRelatedItemDrawer } from '#layers/filters/composables/use-related-item-drawer';
import { resolveFilterComponent } from '#layers/ui/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

/**
 * Opens the item-selection drawer for a relation and builds a primary-key match condition
 * from the chosen items, e.g. `{ author: { id: { _eq: 42 } } }` (to-one) or
 * `{ articles: { _some: { id: { _in: [1, 2, 3] } } } }` (to-many). Nested relation paths nest the
 * full traversal, applying `_some` at each to-many hop.
 */
export function useRelatedItemFilter() {
	const openRelatedItemDrawer = useRelatedItemDrawer();
	const schemaStore = useWorkspaceSchemaStore();
	const filterSchema = injectFilterSchemaContext();
	const fieldTypeTemplates = useFieldTypeTemplates();

	function terminalInlinePicker(relationId: GlobalSingleRelationFieldId) {
		const templateId = filterSchema.findSingleRelationFieldById(relationId)?.meta?.typeTemplateId;
		const template = templateId ? fieldTypeTemplates.value[templateId] : undefined;
		return template?.type === 'relation' ? resolveFilterComponent(template.filter?.picker) : undefined;
	}

	async function buildRelatedItemCondition(relationFieldPath: string[]): Promise<FieldFilter | null> {
		// The path elements are relation field ids; brand the terminal once here so the downstream
		// schema lookups and inline-picker check don't each re-cast it.
		const terminalRelationId = relationFieldPath.at(-1) as GlobalSingleRelationFieldId | undefined;

		if (!terminalRelationId) return null;

		const terminalRelation = schemaStore.findSingleRelationFieldById(terminalRelationId);

		if (!terminalRelation) return null;

		// An M2M field's `oppositeCollection` is the junction, not the target the user picks from.
		// Resolve the real target so the drawer lists actual items and the match traverses
		// source → junction → target rather than matching opaque junction rows.
		const m2m = isM2M(terminalRelation) ? getM2MStructure(terminalRelation) : null;
		const selectionCollection = m2m
			? schemaStore.findCollectionById(m2m.target.collectionId)
			: terminalRelation.oppositeCollection;
		const selectionCollectionId = m2m ? m2m.target.collectionId : terminalRelation.oppositeCollectionId;

		if (!selectionCollection || !selectionCollectionId) return null;

		// M2M is always to-many; otherwise follow the relation's own cardinality.
		const isList = m2m ? true : terminalRelation.isList;

		const primaryKeyFields = getCollectionPrimaryKeyFields(selectionCollection);

		if (primaryKeyFields.length === 0) return null;

		// Wraps a terminal match into the full relation path, adding the M2M junction hop when present.
		function nestMatch(match: FieldFilter): FieldFilter | null {
			const terminalMatch = m2m ? { [m2m.junction.toTarget.apiName]: match } : match;
			return nestFilterThroughRelations(relationFieldPath, terminalMatch, (id) => schemaStore.findSingleRelationFieldById(id));
		}

		// An inline picker (e.g. the user popover) edits keys in place, so seed an empty terminal
		// match; the row detects the inline picker and opens it, with no drawer round-trip on creation.
		if (terminalInlinePicker(terminalRelationId)) {
			const operator = isList ? FilterOperator.IN : FilterOperator.EQ;
			return nestMatch(buildTerminalMatch(operator, [], primaryKeyFields, { positive: true }));
		}

		const selection = await openRelatedItemDrawer(selectionCollectionId, { selection: [] });

		if (!selection || selection.length === 0) return null;

		// A to-many relation (or several picks) is a membership match (`_in`); a single pick on a
		// to-one relation is an identity match (`_eq`). `buildTerminalMatch` is the same serializer the
		// edit path uses, so create and edit produce identical clause shapes.
		const operator = isList || selection.length > 1 ? FilterOperator.IN : FilterOperator.EQ;

		// A non-empty relation path always nests to a single-key relation object, i.e. a `FieldFilter`.
		return nestMatch(buildTerminalMatch(operator, selection, primaryKeyFields, { positive: true }));
	}

	// `openRelatedItemDrawer` is re-exposed so the in-row picker (reopening the drawer to edit an
	// existing related-item match) uses the same injected opener as the creation path.
	return { buildRelatedItemCondition, openRelatedItemDrawer };
}
